import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const BG = '#F4F4F4';
const RED = '#FF1744';
const DARK_RED = '#B00020';
const SOFT_RED = '#C23636';
const NAVY = '#1F2A44';

function safeColor(value, fallback) {
  if (value == null) return fallback;
  const trimmed = String(value).trim();
  return trimmed !== '' ? trimmed : fallback;
}

function buildBrandingPalette(primaryColor, secondaryColor) {
  const primary = safeColor(primaryColor, NAVY);
  const secondary = safeColor(secondaryColor, RED);

  return {
    primary,
    primarySoft: alpha(primary, 0.16),
    secondary,
    secondarySoft: alpha(secondary, 0.2),
    secondaryOutline: alpha(secondary, 0.55),
    secondaryDark: darken(secondary, 0.18),
    panelBorder: alpha(primary, 0.18),
    panelShadow: alpha(primary, 0.18),
    panelGlow: alpha(secondary, 0.12),
  };
}

const shapeMap = {
  left: [
    { size: { xs: 140, sm: 180, md: 220, lg: 280 }, top: { xs: -35, md: -60, lg: -80 }, left: { xs: -50, md: -70, lg: -90 }, tone: 'primary', z: 1 },
    { size: { xs: 110, sm: 130, md: 150, lg: 190 }, top: { xs: -10, md: -20, lg: -35 }, left: { xs: 55, sm: 80, md: 95, lg: 115 }, tone: 'secondary', z: 3 },
    { size: { xs: 110, sm: 135, md: 150, lg: 185 }, top: { xs: 55, md: 70, lg: 85 }, left: { xs: 20, sm: 35, md: 40, lg: 50 }, tone: 'outline', z: 2 },
    { size: { xs: 120, sm: 140, md: 155, lg: 180 }, top: { xs: 150, sm: 180, md: 210, lg: 240 }, left: { xs: 5, sm: 15, md: 20, lg: 30 }, tone: 'secondaryDark', z: 3 },
    { size: { xs: 120, sm: 140, md: 155, lg: 180 }, top: { xs: 170, sm: 200, md: 230, lg: 265 }, left: { xs: 18, sm: 30, md: 40, lg: 55 }, tone: 'outline', z: 2 },
    { size: { xs: 50, sm: 65, md: 75, lg: 95 }, top: { xs: 145, sm: 170, md: 190, lg: 205 }, left: { xs: 145, sm: 180, md: 215, lg: 240 }, tone: 'secondary', z: 3 },
    { size: { xs: 50, sm: 65, md: 75, lg: 95 }, top: { xs: 157, sm: 185, md: 210, lg: 225 }, left: { xs: 150, sm: 185, md: 220, lg: 245 }, tone: 'outline', z: 2 },
    { size: { xs: 20, sm: 24, md: 28, lg: 34 }, top: { xs: 275, sm: 315, md: 350, lg: 385 }, left: { xs: 165, sm: 200, md: 225, lg: 245 }, tone: 'secondary', z: 3 },
    { size: { xs: 20, sm: 24, md: 28, lg: 34 }, top: { xs: 285, sm: 328, md: 365, lg: 400 }, left: { xs: 168, sm: 203, md: 228, lg: 247 }, tone: 'outline', z: 2 },
    { size: { xs: 120, sm: 150, md: 170, lg: 190 }, bottom: { xs: -55, md: -75, lg: -95 }, left: { xs: -30, sm: -20, md: -10, lg: -10 }, tone: 'secondary', z: 3 },
    { size: { xs: 120, sm: 150, md: 170, lg: 190 }, bottom: { xs: -70, md: -95, lg: -120 }, left: { xs: -10, sm: 0, md: 5, lg: 10 }, tone: 'outline', z: 2 },
  ],
  right: [
    { size: { xs: 90, sm: 110, md: 130, lg: 150 }, top: { xs: 90, sm: 100, md: 105, lg: 110 }, right: { xs: 10, sm: 20, md: 30, lg: 40 }, tone: 'outline', z: 2 },
    { size: { xs: 75, sm: 90, md: 100, lg: 120 }, top: { xs: 125, sm: 145, md: 155, lg: 170 }, right: { xs: 0, sm: 8, md: 10, lg: 15 }, tone: 'secondary', z: 3 },
    { size: { xs: 100, sm: 120, md: 140, lg: 160 }, top: { xs: 220, sm: 240, md: 255, lg: 270 }, right: { xs: 70, sm: 90, md: 110, lg: 135 }, tone: 'outline', z: 2 },
    { size: { xs: 80, sm: 95, md: 105, lg: 120 }, top: { xs: 240, sm: 270, md: 285, lg: 300 }, right: { xs: 55, sm: 75, md: 90, lg: 110 }, tone: 'secondary', z: 3 },
    { size: { xs: 18, sm: 22, md: 28, lg: 34 }, top: { xs: 185, sm: 205, md: 220, lg: 230 }, right: { xs: 110, sm: 130, md: 160, lg: 190 }, tone: 'outline', z: 2 },
    { size: { xs: 16, sm: 20, md: 24, lg: 30 }, top: { xs: 235, sm: 255, md: 275, lg: 290 }, right: { xs: 95, sm: 115, md: 140, lg: 165 }, tone: 'secondary', z: 3 },
    { size: { xs: 50, sm: 60, md: 75, lg: 90 }, top: { xs: 330, sm: 355, md: 385, lg: 410 }, right: { xs: 15, sm: 25, md: 35, lg: 55 }, tone: 'secondaryDark', z: 3 },
    { size: { xs: 50, sm: 60, md: 75, lg: 90 }, top: { xs: 295, sm: 315, md: 335, lg: 360 }, right: { xs: -12, sm: -10, md: -12, lg: -15 }, tone: 'secondary', z: 3 },
    { size: { xs: 150, sm: 190, md: 230, lg: 280 }, bottom: { xs: -70, sm: -90, md: -105, lg: -120 }, right: { xs: -55, sm: -70, md: -90, lg: -105 }, tone: 'primary', z: 1 },
    { size: { xs: 120, sm: 150, md: 180, lg: 210 }, bottom: { xs: -45, sm: -55, md: -65, lg: -75 }, right: { xs: 55, sm: 75, md: 100, lg: 125 }, tone: 'secondary', z: 3 },
    { size: { xs: 120, sm: 150, md: 180, lg: 210 }, bottom: { xs: -65, sm: -75, md: -88, lg: -105 }, right: { xs: 38, sm: 58, md: 78, lg: 95 }, tone: 'outline', z: 2 },
  ],
};

function resolveShapeStyles(shape, palette) {
  switch (shape.tone) {
    case 'primary':
      return { bgcolor: palette.primarySoft, border: 'none' };
    case 'secondary':
      return { bgcolor: palette.secondarySoft, border: 'none' };
    case 'secondaryDark':
      return { bgcolor: alpha(palette.secondaryDark, 0.28), border: 'none' };
    case 'outline':
      return { bgcolor: 'transparent', border: `2px solid ${palette.secondaryOutline}` };
    default:
      return { bgcolor: 'transparent', border: 'none' };
  }
}

const DecorativeShapes = ({ position, palette }) => {
  const shapes = shapeMap[position];

  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1 }}>
      {shapes.map((shape, index) => (
        <Box
          key={`${position}-${index}`}
          sx={{
            ...resolveShapeStyles(shape, palette),
            position: 'absolute',
            width: shape.size,
            height: shape.size,
            top: shape.top,
            left: shape.left,
            right: shape.right,
            bottom: shape.bottom,
            borderRadius: '26%',
            transform: 'rotate(45deg)',
            zIndex: shape.z,
            opacity: { xs: 0.9, md: 1 },
          }}
        />
      ))}
    </Box>
  );
};

const AuthFormContainer = ({
  children,
  maxWidth = { xs: 340, sm: 380, md: 420, lg: 460 },
  onSubmit,
  backgroundColor = BG,
  primaryColor = NAVY,
  secondaryColor = RED,
}) => {
  const palette = useMemo(
    () => buildBrandingPalette(primaryColor, secondaryColor),
    [primaryColor, secondaryColor]
  );

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        height: '100dvh',
        width: '100%',
        bgcolor: backgroundColor,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 1.25, sm: 1.75, md: 2.25 },
        backgroundImage: `radial-gradient(circle at top left, ${alpha(
          palette.primary,
          0.1
        )}, transparent 30%), radial-gradient(circle at bottom right, ${alpha(
          palette.secondary,
          0.12
        )}, transparent 30%)`,
      }}
    >
      <DecorativeShapes position="left" palette={palette} />
      <DecorativeShapes position="right" palette={palette} />

      <Box
        component={onSubmit ? 'form' : 'div'}
        onSubmit={onSubmit}
        sx={{
          position: 'relative',
          zIndex: 20,
          width: '100%',
          maxWidth,
          mx: 'auto',
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, sm: 2.25, md: 2.5 },
          borderRadius: { xs: '16px', sm: '20px' },
          border: `1px solid ${palette.panelBorder}`,
          bgcolor: 'rgba(255,255,255,0.96)',
          boxShadow: `0 14px 36px ${palette.panelShadow}, 0 0 0 1px ${palette.panelGlow}`,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: 0,
          maxHeight: 'calc(100dvh - 16px)',
          overflow: 'hidden',
          pb: { xs: 2, sm: 2.25, md: 2.5 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AuthFormContainer;